import { UserError } from "./errors";

const NOOP = () => {};

type LoadingHandlers = {
  leading?: (loading: boolean) => void;
  leadingTrailing?: (loading: boolean) => void;
  trailing?: (loading: boolean) => void;
};

/*
 * This helps manage the "loading" state in components using promises (usually API
 * requests), also helping deal with failed promises by triggering `UserErrors` (which
 * show an error overlay for users)
 *
 * - `promise`: a promise, usually created by calling a method in `utils/fetch`
 * - `loadingHandlers`: this lets you control when your loading state is updated during
 *    the promise lifecycle. The methods you pass in will be called with the current loading
 *    state (e.g. `true` when loading and `false` when finished). Use `leading` if you
 *    only want to be notified when loading starts, use `trailing` if you only want to be
 *    notified when loading ends and `leadingTrailing` for both. **All of these will be
 *    called on an exception** (with `false` e.g. loading finished).
 */
export default async <T>(
  promise: Promise<T>,
  loadingHandlers: LoadingHandlers = {},
) => {
  const {
    leading = NOOP,
    trailing = NOOP,
    leadingTrailing = NOOP,
  } = loadingHandlers;

  try {
    leading(true);
    leadingTrailing(true);

    const result = await promise;

    trailing(false);
    leadingTrailing(false);

    return result;
  } catch (e) {
    leading(false);
    trailing(false);
    leadingTrailing(false);

    if (e instanceof Error) {
      throw new UserError(e.message, { cause: e });
    } else {
      throw new UserError("An error occurred", { cause: e });
    }
  }
};
